import { dispatchPromise, fetchResources } from './action-helpers';

import { showModal } from './modal';

import { getInitials } from '../utils/helpers';
import { getCurrentUserId, getCurrentTeam } from '../services/store-service';
import { setUserTrackingProp, trackEvent } from '../services/events-tracker-service';
import { createAlert } from '../services/alert-service';

const DEFAULT_PROJECT_PROPS = {
    archived: false,
    color: '#ccc',
    demo: false,
    id: null,
    initials: '',
    members: [],
    owner: null,
    name: '',
    progress: 0,
    secondary_color: '#ccc',
    disabled_modules: []
};

const handleInvitees = invitees => {
    const users = invitees.filter(user => !user.id);

    createAlert('success', `Invitation to ${invitees.length > 3 ? invitees.length + ' users' : invitees.map(i => i.name || i.email).join(', ')} has been sent.`, {
        duration: 3000
    });

    if (users.length) {
        users.forEach(user => {
            trackEvent('USER.INVITE', {
                via: 'Project',
                role: user.role
            });
        });

        setUserTrackingProp('has_invited_users', true);
    }
}

export const showCreateProjectModal = ({ sourceId, type, subteamId } = {}) => {
    showModal('CREATE_PROJECT', { sourceId, type, subteamId });
}

export const showArchiveProjectModal = (projectId, projectTitle) => {
    showModal('ARCHIVE_PROJECT', {
        projectId,
        projectTitle
    });
}

export const fetchProjects = (archived, all) => fetchResources('PROJECTS_FETCH_REQUESTED', { archived }, all);

export const createProject = projectProps => {
    const currentUserId = getCurrentUserId();
    const ownerId = getCurrentTeam().owner;

    if (currentUserId !== ownerId) {
        if (!('members' in projectProps)) {
            projectProps.members = [];
        }

        projectProps.members.push(ownerId);
    }

    if (projectProps.name) {
        projectProps.initials = getInitials(projectProps.name);
    }

    return dispatchPromise({
        type: 'PROJECT_CREATE_REQUESTED',
        payload: {
            project: Object.assign({}, DEFAULT_PROJECT_PROPS, projectProps, {
                owner: currentUserId
            })
        }
    }).then(({ project_id: projectId }) => {
        setUserTrackingProp('created_first_project', true);

        if (projectProps.invitees && projectProps.invitees.length) {
            return inviteToProject(projectId, projectProps.invitees).then(() => projectId);
        }

        return projectId;
    });
}

export const fetchProject = projectId => {
    return dispatchPromise({
        type: 'PROJECT_FETCH_REQUESTED',
        payload: {
            projectId
        }
    });
}

export const updateProject = (projectId, projectProps, preventSave) => {
    if (projectProps.name) {
        projectProps.initials = getInitials(projectProps.name);
    }

    return dispatchPromise({
        type: 'PROJECT_UPDATE_REQUESTED',
        preventSave,
        payload: {
            projectId,
            projectProps
        }
    });
}

export const removeProject = projectId => {
    return dispatchPromise({
        type: 'PROJECT_REMOVE_REQUESTED',
        payload: { projectId }
    });
}

export const duplicateProject = (projectId, duplicateOptions) => {
    if (duplicateOptions.name) {
        duplicateOptions.initials = getInitials(duplicateOptions.name);
    }

    return dispatchPromise({
        type: 'PROJECT_DUPLICATE_REQUESTED',
        payload: {
            projectId,
            duplicateOptions
        }
    }).then(projectId => {
        setUserTrackingProp('created_first_project', true);

        return projectId;
    })
}

export const inviteToProject = (projectId, users) => {
    return dispatchPromise({
        type: 'PROJECT_INVITE_USERS_REQUESTED',
        payload: {
            projectId,
            users
        }
    }).then(response => {
        handleInvitees(users);
        return response;
    })
}

export const joinProject = projectId => {
    return dispatchPromise({
        type: 'PROJECT_JOIN_REQUESTED',
        payload: { projectId }
    });
}

export const leaveProject = projectId => {
    return dispatchPromise({
        type: 'PROJECT_LEAVE_REQUESTED',
        payload: { projectId }
    });
}

export const startProject = projectId => {
    return dispatchPromise({
        type: 'PROJECT_START_REQUESTED',
        payload: { projectId }
    });
}

export const enableProjectModule = (projectId, module) => {
    return dispatchPromise({
        type: 'PROJECT_MODULE_ENABLE_REQUESTED',
        payload: { projectId, module }
    });
}

export const disableProjectModule = (projectId, module) => {
    return dispatchPromise({
        type: 'PROJECT_MODULE_DISABLE_REQUESTED',
        payload: { projectId, module }
    });
}

export const linkProjectRepo = (projectId, repoName) => {
    return dispatchPromise({
        type: 'PROJECT_REPO_LINK_REQUESTED',
        payload: { projectId, repoName }
    });
}